// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Star Systems Configuration - Universe Delta (Improbability Sector)
 * 
 * A universe where the Infinite Improbability Drive went horribly right.
 * Systems named after Hitchhiker's Guide references.
 * Resources are absurd. Threats are improbable. Reality is negotiable.
 */

export const starSystems = [
  {
    id: 300,
    name: 'MAGRATHEA',
    distance: 0, // Starting system
    threat: 'MOSTLY HARMLESS',
    resources: 'CUSTOM PLANETS',
    services: ['refuel', 'trade', 'weapons', 'exploration'],
    description: 'Ancient planet factory. Currently closed for business. The mice are still here though.',
    primaryColor: '#ffff00',
    secondaryColor: '#00ff00',
    hasRings: false,
    animated: true,
  },
  {
    id: 301,
    name: 'BETELGEUSE SEVEN',
    distance: 42, // Of course
    threat: 'VOGON POETRY',
    resources: 'BUREAUCRACY',
    services: ['refuel', 'trade', 'weapons'],
    description: 'Home of Ford Prefect. Avoid the Vogon poetry readings at all costs.',
    primaryColor: '#ff00ff',
    secondaryColor: '#ffff00',
    hasRings: false,
    animated: true,
  },
  {
    id: 302,
    name: 'FROGSTAR WORLD B',
    distance: 100,
    threat: 'TOTAL PERSPECTIVE',
    resources: 'EXISTENTIAL DREAD',
    services: ['refuel', 'trade', 'weapons'],
    description: 'Location of the Total Perspective Vortex. Not recommended for those with fragile egos.',
    primaryColor: '#00ff00',
    secondaryColor: '#ff00ff',
    hasRings: true,
    animated: true,
  },
  {
    id: 303,
    name: 'MILLIWAYS',
    distance: 150,
    threat: 'NONE',
    resources: 'FINE DINING',
    services: ['refuel', 'trade', 'weapons', 'upgrades'],
    description: 'The Restaurant at the End of the Universe. Reservations recommended. Watch the universe end in style.',
    primaryColor: '#ffff00',
    secondaryColor: '#ff8800',
    hasRings: false,
    animated: true,
  },
  {
    id: 304,
    name: 'DAMOGRAN',
    distance: 200,
    threat: 'DEMOLITION',
    resources: 'FJORDS',
    services: ['refuel', 'trade', 'exploration'],
    description: 'Mostly swamp and fjords. Scheduled for demolition to make way for hyperspace bypass.',
    primaryColor: '#00ff88',
    secondaryColor: '#0088ff',
    hasRings: false,
    animated: false,
  },
  {
    id: 305,
    name: 'KAKRAFOON',
    distance: 250,
    threat: 'SILENCE',
    resources: 'TELEPATHY',
    services: ['refuel', 'trade', 'weapons'],
    description: 'Planet of loud minds. Everyone is telepathic. Bring earplugs for your brain.',
    primaryColor: '#ff00ff',
    secondaryColor: '#00ffff',
    hasRings: false,
    animated: true,
  },
  {
    id: 306,
    name: 'URSA MINOR BETA',
    distance: 300,
    threat: 'PUBLISHING',
    resources: 'GUIDEBOOKS',
    services: ['refuel', 'trade'],
    description: 'Publishing headquarters of the Hitchhiker\'s Guide to the Galaxy. Don\'t Panic.',
    primaryColor: '#ffff00',
    secondaryColor: '#00ff00',
    hasRings: false,
    animated: true,
  },
  {
    id: 307,
    name: 'SQORNSHELLOUS ZETA',
    distance: 350,
    threat: 'MATTRESSES',
    resources: 'SWAMP',
    services: ['refuel', 'trade', 'exploration'],
    description: 'Swamp planet inhabited entirely by mattresses. Very bouncy. Surprisingly philosophical.',
    primaryColor: '#88ff00',
    secondaryColor: '#00ff88',
    hasRings: false,
    animated: false,
  },
  {
    id: 308,
    name: 'PRELIUMTARN',
    distance: 420, // Nice
    threat: 'IMPROBABLE',
    resources: 'PROBABILITY',
    services: ['refuel', 'weapons', 'upgrades'],
    description: 'Home of the Infinite Improbability Drive. Things that shouldn\'t happen, do. Often.',
    primaryColor: '#ff00ff',
    secondaryColor: '#ffff00',
    hasRings: true,
    animated: true,
  },
  {
    id: 309,
    name: 'BARTLEDAN',
    distance: 500,
    threat: 'SHOE SHOPS',
    resources: 'FOOTWEAR',
    services: ['refuel', 'trade', 'weapons'],
    description: 'Planet covered entirely in shoe shops. The economy makes no sense. Nobody questions it.',
    primaryColor: '#ff8800',
    secondaryColor: '#ffff00',
    hasRings: false,
    animated: false,
  },
];

// Space Stations in the Improbability Universe
export const spaceStations = [
  {
    id: 400,
    name: 'DEEP THOUGHT STATION',
    distance: 75,
    threat: 'COMPUTATIONAL',
    resources: 'ANSWERS TO EVERYTHING',
    services: ['refuel', 'trade', 'weapons', 'upgrades', 'modules'],
    description: 'Massive computer station still calculating the Ultimate Question. Excellent tech services.',
    type: 'station',
    animated: true,
  },
  {
    id: 401,
    name: 'HEART OF GOLD DEPOT',
    distance: 180,
    threat: 'IMPROBABLE',
    resources: 'INFINITE IMPROBABILITY',
    services: ['refuel', 'trade', 'weapons', 'upgrades', 'modules'],
    description: 'Refitting station for Infinite Improbability Drive ships. Expect the unexpected.',
    type: 'station',
    animated: true,
  },
  {
    id: 402,
    name: 'SIRIUS CYBERNETICS OUTPOST',
    distance: 320,
    threat: 'CHEERFUL',
    resources: 'GENUINE PEOPLE PERSONALITIES',
    services: ['refuel', 'trade', 'modules'],
    description: 'Corporate station selling doors with Genuine People Personalities. Share and Enjoy!',
    type: 'station',
    animated: true,
  },
  {
    id: 403,
    name: 'GOLGAFRINCHAN ARK STATION',
    distance: 450,
    threat: 'BUREAUCRACY',
    resources: 'MIDDLE MANAGEMENT',
    services: ['refuel', 'trade', 'weapons', 'upgrades'],
    description: 'Station run by telephone sanitizers and account executives. Surprisingly efficient.',
    type: 'station',
    animated: true,
  },
];

export const starPositions = starSystems.reduce((acc, system) => {
  if (system.coordinates) {
    acc[system.id] = system.coordinates;
  }
  return acc;
}, {});

